import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { showAuth } from '../redux/authSlice';
import { fetchSavedProblems } from '../api/queries';
import { formatProblemTitle } from '../common/utils';
import proxy from '../api/axios';
import { getCachedBookmark, setCachedBookmark } from '../common/bookmarkCache';
import { patchProblemBookmarkInCache } from '../api/queries';

const ACCENT = '#137274';

const DIFFICULTY_COLORS = {
  easy: '#1a7f37',
  medium: '#b45309',
  hard: '#cf222e',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'bookmarked', label: 'Bookmarked' },
  { id: 'notes', label: 'Has notes' },
];

const DISCOVER_FEATURES = [
  {
    to: '/practice-track',
    icon: RouteOutlinedIcon,
    title: 'Practice Track',
    description: 'A structured, level-by-level DSA path — pick up where you left off.',
  },
  {
    to: '/contests',
    icon: EmojiEventsOutlinedIcon,
    title: 'Contests',
    description: 'Compete live against other students and climb the leaderboard.',
  },
  {
    to: '/live-coding',
    icon: VideocamOutlinedIcon,
    title: 'Live Coding Room',
    description: 'Pair up and solve problems together in real time.',
  },
  {
    to: '/mentorship',
    icon: SupportAgentOutlinedIcon,
    title: 'Mentorship & Mock Interviews',
    description: 'Book 1:1 sessions with mentors to prep for interviews.',
  },
];

const formatUpdatedAt = (date) => {
  if (!date) return null;
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
};

const SavedProblemsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSavedProblems(filter)
      .then((data) => {
        if (cancelled) return;
        // A locally cached toggle (made on this device, maybe still in
        // flight elsewhere) is more current than what just came back.
        setItems(
          data.map((item) => {
            const cached = getCachedBookmark(item.slug);
            return cached !== null ? { ...item, isBookmarked: cached } : item;
          }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError('Could not load your saved problems');
        toast.error(err.response?.data?.message || 'Could not load saved problems');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, isLoggedIn]);

  const handleToggleBookmark = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const { slug } = item;
    const previous = item.isBookmarked;
    const next = !previous;

    // Optimistic — flip instantly, same pattern as the problem page.
    setCachedBookmark(slug, next);
    patchProblemBookmarkInCache(slug, next);
    setItems((prev) => prev.map((p) => (p.slug === slug ? { ...p, isBookmarked: next } : p)));

    try {
      const { data } = await proxy.post(`/problems/${slug}/bookmark`);
      setCachedBookmark(slug, data.isBookmarked);
      patchProblemBookmarkInCache(slug, data.isBookmarked);
      setItems((prev) =>
        prev.map((p) => (p.slug === slug ? { ...p, isBookmarked: data.isBookmarked } : p)),
      );
    } catch (err) {
      setCachedBookmark(slug, previous);
      patchProblemBookmarkInCache(slug, previous);
      setItems((prev) =>
        prev.map((p) => (p.slug === slug ? { ...p, isBookmarked: previous } : p)),
      );
      toast.error(err.response?.data?.message || 'Could not update bookmark');
    }
  };

  const chipSx = (active) => ({
    fontWeight: 700,
    borderRadius: 1.5,
    border: `1px solid ${active ? ACCENT : border}`,
    color: active ? ACCENT : muted,
    bgcolor: active ? alpha(ACCENT, isDark ? 0.18 : 0.1) : 'transparent',
  });

  const emptyMessage = useMemo(() => {
    if (filter === 'bookmarked') return "You haven't bookmarked any problems yet.";
    if (filter === 'notes') return "You haven't written any notes yet.";
    return "You haven't saved any problems yet.";
  }, [filter]);

  const featureDiscovery = (
    <Box sx={{ mt: 4 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25 }}>
        More to explore on CrafterHack
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1,
        }}
      >
        {DISCOVER_FEATURES.map(({ to, icon: Icon, title, description }) => (
          <Box
            key={to}
            component={RouterLink}
            to={to}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${border}`,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'border-color 0.15s ease, background 0.15s ease',
              '&:hover': {
                borderColor: alpha(ACCENT, 0.4),
                bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.04),
              },
            }}
          >
            <Icon sx={{ fontSize: 22, color: ACCENT, flexShrink: 0, mt: 0.25 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: muted, fontSize: '0.78rem', mt: 0.25 }}>
                {description}
              </Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ fontSize: 18, color: muted, flexShrink: 0, mt: 0.5 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (!isLoggedIn) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 10 } }}>
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 3,
            borderRadius: 3,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? '#0d1117' : '#f8fafb',
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 44, color: muted, mb: 2, opacity: 0.65 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.75 }}>
            Sign in to see your saved problems
          </Typography>
          <Typography variant="body2" sx={{ color: muted, maxWidth: 360, mx: 'auto', mb: 2.5 }}>
            Bookmarks and notes are private to your account.
          </Typography>
          <Button
            variant="contained"
            onClick={() => dispatch(showAuth())}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
            }}
          >
            Sign in
          </Button>
        </Box>
        {featureDiscovery}
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', sm: '1.6rem' }, letterSpacing: '-0.02em' }}>
          Saved Problems
        </Typography>
        <Typography variant="body2" sx={{ color: muted, mt: 0.5 }}>
          Your bookmarked problems and notes, in one place.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.id}
            label={f.label}
            clickable
            size="small"
            onClick={() => setFilter(f.id)}
            sx={chipSx(filter === f.id)}
          />
        ))}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: ACCENT }} />
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ color: '#cf222e', textAlign: 'center', py: 4 }}>{error}</Typography>
      )}

      {!loading && !error && items.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? '#0d1117' : '#f8fafb',
          }}
        >
          <BookmarkBorderRoundedIcon sx={{ fontSize: 40, color: muted, mb: 1.5, opacity: 0.65 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{emptyMessage}</Typography>
          <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
            Bookmark a problem or jot a note from its page to see it here.
          </Typography>
          <Button
            component={RouterLink}
            to="/problems"
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: border, color: ACCENT }}
          >
            Browse problems
          </Button>
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item) => (
            <Box
              key={item.slug}
              onClick={() => navigate(`/problems/${item.slug}`)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${border}`,
                bgcolor: surface,
                cursor: 'pointer',
                color: 'inherit',
                transition: 'border-color 0.15s ease, background 0.15s ease',
                '&:hover': {
                  borderColor: alpha(ACCENT, 0.4),
                  bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.04),
                },
              }}
            >
              <Tooltip title={item.isBookmarked ? 'Remove bookmark' : 'Bookmark'} arrow>
                <IconButton
                  size="small"
                  onClick={(e) => handleToggleBookmark(e, item)}
                  aria-label={item.isBookmarked ? 'Remove bookmark' : 'Bookmark problem'}
                  sx={{ p: 0.25, mt: 0.1, flexShrink: 0 }}
                >
                  {item.isBookmarked ? (
                    <BookmarkRoundedIcon sx={{ fontSize: 20, color: ACCENT }} />
                  ) : (
                    <BookmarkBorderRoundedIcon sx={{ fontSize: 20, color: muted }} />
                  )}
                </IconButton>
              </Tooltip>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    {formatProblemTitle(item.title)}
                  </Typography>
                  {item.difficulty && (
                    <Chip
                      size="small"
                      label={item.difficulty.charAt(0).toUpperCase() + item.difficulty.slice(1)}
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        fontSize: '0.62rem',
                        '& .MuiChip-label': { px: 0.7 },
                        bgcolor: alpha(DIFFICULTY_COLORS[item.difficulty] || muted, isDark ? 0.2 : 0.1),
                        color: DIFFICULTY_COLORS[item.difficulty] || muted,
                      }}
                    />
                  )}
                  {item.section && (
                    <Typography variant="caption" sx={{ color: muted }}>
                      {item.section}
                    </Typography>
                  )}
                </Box>
                {item.notePreview && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: muted,
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.notePreview}
                  </Typography>
                )}
              </Box>

              <Typography variant="caption" sx={{ color: muted, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {formatUpdatedAt(item.updatedAt)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {featureDiscovery}
    </Container>
  );
};

export default SavedProblemsPage;
